import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import { Routes, Route, BrowserRouter } from "react-router";
import { AuthProvider } from './components/AuthProvider';
import { CookieConsentProvider } from './context/CookieConsentContext';
import { CategoriesProvider } from './context/CategoriesContext';
import { NotificationProvider } from './context/NotificationContext';
import { FavoriteStateProvider } from './context/FavoriteStateContext';
import ErrorBoundary from './components/ErrorBoundary';
import Home from './pages/Home';
import Search from './pages/Search';
import ProtectedRoute from './components/ProtectedRoute';
import AdminLayout from './layouts/AdminLayout';
//import reportWebVitals from './reportWebVitals';
import LoadingComponent from "./components/LoadingComponent"

const root = document.getElementById("root");

const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const SetPassword = lazy(() => import('./pages/SetPassword'));
const Listing = lazy(() => import('./pages/Listing'));
const CreateListing = lazy(() => import('./pages/CreateListing'));
const EditListing = lazy(() => import('./pages/EditListing'));
const UserListing = lazy(() => import('./pages/UserListing'));
const UserFavorite = lazy(() => import('./pages/UserFavorite'));
const MyAccount = lazy(() => import('./pages/MyAccount'));
const PaymentReturn = lazy(() => import('./pages/PaymentReturn'));
const Messages = lazy(() => import('./pages/Messages'));
const Conversation = lazy(() => import('./pages/Conversation'));

// Admin pages
const CacheManagement = lazy(() => import('./pages/admin/CacheManagement'));
const CategoryManagement = lazy(() => import('./pages/admin/CategoryManagement'));
const UserManagement = lazy(() => import('./pages/admin/UserManagement'));
const ListingManagement = lazy(() => import('./pages/admin/ListingManagement'));
const AlertDialogSettings = lazy(() => import('./pages/admin/AlertDialogSettings'));
const SeoSettings = lazy(() => import('./pages/admin/SeoSettings'));
const Settings = lazy(() => import('./pages/admin/Settings'));
const AdminTest = lazy(() => import('./pages/admin/AdminTest'));
const PaymentManagement = lazy(() => import('./pages/admin/PaymentManagement'));
const PricingManagement = lazy(() => import('./pages/admin/PricingManagement'));

ReactDOM.createRoot(root).render(
  <ErrorBoundary>
    <BrowserRouter>
      <NotificationProvider>
        <AuthProvider>
        <CookieConsentProvider>
        <CategoriesProvider>
        <FavoriteStateProvider>
        <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/search" element={<Search />}/>
        <Route path="/sign/in" element={<Suspense fallback={<LoadingComponent />}><Login /></Suspense>} />
        <Route path="/sign/up" element={<Suspense fallback={<LoadingComponent />}><Register /></Suspense>} />
        <Route path="/verify" element={<Suspense fallback={<LoadingComponent />}><VerifyEmail /></Suspense>} />
        <Route path="/forgot-password" element={<Suspense fallback={<LoadingComponent />}><ForgotPassword /></Suspense>} />
        <Route path="/reset-password" element={<Suspense fallback={<LoadingComponent />}><ResetPassword /></Suspense>} />
        <Route
          path="/set-password"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingComponent />}><SetPassword /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route path="/listing/:listingId" element={<Suspense fallback={<LoadingComponent />}><Listing /></Suspense>} />
        <Route path="/payment/return" element={<Suspense fallback={<LoadingComponent />}><PaymentReturn /></Suspense>} />
        <Route path="/test" element={<Suspense fallback={<LoadingComponent />}><CreateListing /></Suspense>} />
        {/* Trasa chroniona */}
        <Route
          path="/user/listing"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingComponent />}><UserListing /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/favorite"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingComponent />}><UserFavorite /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/account"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingComponent />}><MyAccount /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/messages"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingComponent />}><Messages /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/messages/:conversationId"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingComponent />}><Conversation /></Suspense>
            </ProtectedRoute>
          }
        />

        {/* Admin Routes */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route path="cache" element={<Suspense fallback={<LoadingComponent />}><CacheManagement /></Suspense>} />
          <Route path="categories" element={<Suspense fallback={<LoadingComponent />}><CategoryManagement /></Suspense>} />
          <Route path="users" element={<Suspense fallback={<LoadingComponent />}><UserManagement /></Suspense>} />
          <Route path="listings" element={<Suspense fallback={<LoadingComponent />}><ListingManagement /></Suspense>} />
          <Route path="alerts" element={<Suspense fallback={<LoadingComponent />}><AlertDialogSettings /></Suspense>} />
          <Route path="settings" element={<Suspense fallback={<LoadingComponent />}><Settings /></Suspense>} />
          <Route path="settings/seo" element={<Suspense fallback={<LoadingComponent />}><SeoSettings /></Suspense>} />
          <Route path="payments" element={<Suspense fallback={<LoadingComponent />}><PaymentManagement /></Suspense>} />
          <Route path="pricing" element={<Suspense fallback={<LoadingComponent />}><PricingManagement /></Suspense>} />
          <Route path="test" element={<Suspense fallback={<LoadingComponent />}><AdminTest /></Suspense>} />
        </Route>
        <Route
          path="/user/listing/create"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingComponent />}><CreateListing /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/listing/edit/:id"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingComponent />}><EditListing /></Suspense>
            </ProtectedRoute>
          }
        />
        </Routes>
        </FavoriteStateProvider>
        </CategoriesProvider>
        </CookieConsentProvider>
        </AuthProvider>
      </NotificationProvider>
    </BrowserRouter>
  </ErrorBoundary>

);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
//reportWebVitals();
